import React, { type FC } from 'react';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import {
  Button,
  HoverStyles,
  ButtonPriority as PRIORITY,
  ButtonSize as SIZE,
} from 'wix-ui-tpa/cssVars';
import { SpinnerWrapper } from '@wix/bookings-viewer-ui';
import settingsParams from '../../../../../settingsParams';
import { ENABLE_BOOK_BUTTON_SPACE_KEY } from '../../../../../../../consts';
import { ButtonStyleOptions } from '../../../../../../../types/types';
import { useWidgetViewModel } from '../../../../hooks/useWidgetViewModel';
import stylesParams from '../../../../../stylesParams';
import { DataHooks } from './consts';
import { classes, st } from './BookButton.st.css';

export interface BookButtonProps {
  onClick: () => void;
  url: string | undefined;
  loading?: boolean;
  isNoBookFlow: boolean;
  isPendingApprovalFlow: boolean;
  showViewCourse?: boolean;
  serviceId: string;
}

export const BookButton: FC<BookButtonProps> = ({
  onClick,
  url,
  isNoBookFlow,
  isPendingApprovalFlow,
  showViewCourse,
  loading,
  serviceId,
}) => {
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const { shouldWorkWithAppSettings } = useWidgetViewModel();
  const settings = useSettings();
  const styles = useStyles();
  const theme = settings.get(settingsParams.bookButtonStyle);

  const isSecondary =
    shouldWorkWithAppSettings &&
    (theme === ButtonStyleOptions.SQUARE_HOLE ||
      theme === ButtonStyleOptions.CIRCLE_HOLE);

  const spinnerDiameter = isMobile
    ? styles.get(stylesParams.mobileBookButtonFontSize)
    : styles.get(stylesParams.bookButtonFont).size!;

  const getContent = () => {
    if (showViewCourse) {
      return (
        settings.get(settingsParams.viewCourseButtonText) ||
        settings.getDefaultValue(settingsParams.viewCourseButtonText)
      );
    }
    if (isNoBookFlow) {
      return (
        settings.get(settingsParams.noBookFlowText) ||
        settings.getDefaultValue(settingsParams.noBookFlowText)
      );
    }
    if (isPendingApprovalFlow) {
      return (
        settings.get(settingsParams.pendingApprovalText) ||
        settings.getDefaultValue(settingsParams.pendingApprovalText)
      );
    }
    return (
      settings.get(settingsParams.bookButtonText) ||
      settings.getDefaultValue(settingsParams.bookButtonText)
    );
  };

  const isLink = !!url;

  const activate = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClick();
  };

  // When a url is given the button renders as an <a>, which browsers do not
  // activate with Space. Since it keeps role="button", Space has to work here.
  const onKeyDown =
    experiments.enabled(ENABLE_BOOK_BUTTON_SPACE_KEY) && isLink && !loading
      ? (e: React.KeyboardEvent) => {
          if (e.key !== ' ' && e.key !== 'Spacebar') {
            return;
          }
          if (e.repeat) {
            // Holding Space repeats keydown. Keep blocking the page scroll,
            // but book only on the first event.
            e.preventDefault();
            return;
          }
          activate(e);
        }
      : undefined;

  return (
    <div
      data-hook={DataHooks.ROOT}
      className={st(classes.root, {
        theme,
        isMobile,
        shouldWorkWithAppSettings,
      })}
    >
      <Button
        data-hook={DataHooks.BUTTON}
        className={st(classes.button)}
        size={SIZE.medium}
        priority={isSecondary ? PRIORITY.secondary : PRIORITY.primary}
        onClick={isLink ? activate : onClick}
        onKeyDown={onKeyDown}
        fullWidth={isMobile}
        aria-label={getContent()}
        aria-describedby={serviceId}
        role="button"
        tabIndex={0}
        disabled={loading}
        upgrade
        {...(url ? { as: 'a', href: url } : undefined)}
        hoverStyle={HoverStyles.underline}
      >
        {loading ? (
          <div className={classes.spinnerWrapper}>
            <SpinnerWrapper
              className={classes.spinner}
              diameter={spinnerDiameter}
              data-hook={DataHooks.SPINNER}
            />
          </div>
        ) : (
          getContent()
        )}
      </Button>
    </div>
  );
};
